import { DownOutlined, PlusOutlined } from "@ant-design/icons"
import { Badge, Button, type ButtonProps, Dropdown, Flex } from "antd"
import React, { useCallback, useEffect, useState } from "react"

import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { CreateProjectWizard } from "../../Project/ProjectAddPage/wizard/CreateProjectWizard.tsx"
import * as services from "../api/services"
import ProjectsLeadApproval from "./ProjectsLeadApproval.tsx"

interface ProjectsLeadApprovalTriggerProps {
  onRefetchCallback: () => void
}

export function ProjectsLeadApprovalTrigger({
  onRefetchCallback
}: ProjectsLeadApprovalTriggerProps) {
  const canConvertLeadToProject = useAppSelector(
    (state) =>
      state?.auth?.userInfo?.permissions?.codes?.projects?.features
        ?.project_approval
  )

  const [openApprovalModal, setOpenApprovalModal] = useState(false)
  const [openWizard, setOpenWizard] = useState(false)
  // `null` is "not fetched", which the badge withholds rather than rendering
  // as a confident `0`. Local rather than in the projects slice: nothing else
  // reads it, and a slice field would have to be imported by the reducer,
  // which every page-level test that mocks `projects.thunks` would then need
  // to know about.
  const [approvalCount, setApprovalCount] = useState<number | null>(null)

  const toggleOpenModal = () => setOpenApprovalModal(!openApprovalModal)

  // The same queue the modal lists, asked only for its size — `page_size: 1`
  // because nothing here reads the rows. Silent on failure on purpose: this
  // is a number on a menu item, and an error toast for it would be worse than
  // the badge simply not appearing.
  const refreshApprovalCount = useCallback(async () => {
    if (!canConvertLeadToProject) return

    const response = await services.getLeadsApproval({ page: 1, page_size: 1 })

    if (response.ok) setApprovalCount(response.data?.count ?? 0)
  }, [canConvertLeadToProject])

  // Up front rather than when the dropdown opens, so the number is already
  // right when the item appears instead of popping in a beat later.
  useEffect(() => {
    void refreshApprovalCount()
  }, [refreshApprovalCount])

  const onCloseHandler = (refetch = false) => {
    if (refetch) onRefetchCallback()
    setOpenApprovalModal(false)
    // Approving a lead is the one thing that empties this queue, and it only
    // happens inside the modal — so closing it is when the number is stale.
    void refreshApprovalCount()
  }

  const onWizardClose = () => setOpenWizard(false)

  const buttonProps: ButtonProps = {
    onClick: () => setOpenWizard(true),
    type: "primary",
    className: "w-auto",
    icon: <PlusOutlined />
  }

  return (
    <>
      {canConvertLeadToProject ? (
        <Dropdown.Button
          {...buttonProps}
          icon={<DownOutlined />}
          menu={{
            items: [
              {
                key: "from_lead",
                icon: <PlusOutlined />,
                label: (
                  <Flex align={"center"} gap={8}>
                    Create from lead
                    {approvalCount !== null && (
                      <Badge count={approvalCount} size={"small"} showZero />
                    )}
                  </Flex>
                ),
                onClick: toggleOpenModal
              }
            ]
          }}
        >
          <PlusOutlined /> New Project
        </Dropdown.Button>
      ) : (
        <Button {...buttonProps} icon={<PlusOutlined />}>
          New Project
        </Button>
      )}

      {/*@todo add lazy + suspense */}
      {openApprovalModal && <ProjectsLeadApproval onClose={onCloseHandler} />}
      {openWizard && <CreateProjectWizard onClose={onWizardClose} />}
    </>
  )
}
